<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col>
      <card-title-row
        style="font-size: 18px !important"
        translation-path="sidebar.project"
        :amount="1"
        :is-plural="true"
        :class="{ 'no-print': isQuickPrint }"
      >
        <template #postTitle>
          <v-subheader
            style="font-style: italic; font-size: 13px; opacity: 0.8"
            v-text="isUnsavedChangeTextVisible"
          ></v-subheader>
        </template>
        <template #buttons>
          <base-rounded-button
            icon="mdi-content-save"
            size="x-small"
            @click="isAddLayerDisabled ? openCreateProjectDialog() : saveProjectEditing()"
            :tooltip="saveProjectTooltip"
            :disabled="isSaveButtonDisabled"
            :loading="projectEditingLoading"
          />
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :disabled="!isProjectLayersFetched || isModifySnappingActive"
            @click="showCreateProjectDialog"
            :tooltip="createProjectTooltip"
          />
        </template>
      </card-title-row>
      <sidebar-row class="pt-2" :class="{ 'no-print': isQuickPrint }">
        <v-row no-gutters class="pr-1 pb-6 border">
          <v-col cols="5">
            <v-tooltip right color="font">
              <template #activator="{ on }">
                <span v-on="on">
                  <data-input
                    disabled-sorting
                    :menu-props="{ maxWidth: 350, offsetY: true }"
                    ref="select"
                    @click.prevent="isDisplayWarning && !isProjectDefault ? showWarning() : null"
                    @change="projectChanged"
                    @blur="!projectProxy ? (projectProxy = JSON.parse(JSON.stringify(project))) : null"
                    v-model="projectProxy"
                    :items="projectsArray"
                    return-object
                    item-text="name"
                    item-value="id"
                    :no-data-text="$i18n.tc('sidebar.project', 0)"
                    :data-type="dataTypeSelect"
                    :disabled="isModifySnappingActive"
                    hide-details
                  >
                  </data-input>
                </span>
              </template>
              {{ changeProjectTooltip }}
            </v-tooltip>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" class="pt-2">
            <dots-menu
              v-if="!isProjectDefault && isProjectLayersFetched"
              :items="dotsMenuItems"
              @delete="showProjectDeleteDialog"
              @changeName="changeProjectName"
              @share="showShareDialog"
              @copy="showCreateProjectDialog($event, true)"
            ></dots-menu>
          </v-col>
        </v-row>
      </sidebar-row>
      <template v-if="isProjectLayersFetched">
        <card-title-row
          class="pt-6 pb-1"
          :class="{ 'no-print': isQuickPrint }"
          translation-path="sidebar.layers"
          :is-subtitle="true"
          :amount="2"
          :is-plural="true"
        >
          <template #buttons>
            <base-rounded-button
              :disabled="isAddLayerDisabled"
              :tooltip="addGroupTooltip"
              icon="mdi-folder-multiple-plus"
              size="x-small"
              @click="showAddGroupDialog"
            />
            <base-rounded-button
              :disabled="isAddLayerDisabled"
              :tooltip="addLayerTooltip"
              icon="mdi-plus"
              size="x-small"
              color="success"
              @click="showAddLayersDialog"
            />
          </template>
        </card-title-row>
        <sidebar-row
          :has-icon-column="layers.find(layer => layer.layers && layer.layers.length > 0) ? true : false"
          :slotCols="layers.find(layer => layer.layers && layer.layers.length > 0) ? '11' : '12'"
        >
          <legend-layers-list
            :class="[
              {
                'pl-3': layers.find(layer => layer.layers && layer.layers.length > 0) ? false : true,
              },
              { 'print-legend_layers': isQuickPrint },
            ]"
            key="project"
            @hasUnsavedChanges="hasUnsavedChanges = $event"
            v-model="layers"
            :is-osm-item-visible="true"
            :is-manipulation-disabled="isAddLayerDisabled"
          ></legend-layers-list>
        </sidebar-row>
      </template>
      <main-loader v-else class="pt-10" />
    </v-col>
    <main-large-dialog
      v-if="isChangeNameDialogLoaded"
      :is-visible.sync="isChangeNameDialogVisible"
      :title="$i18n.t('dialog.changeProjectName')"
      agree-text="dialog.agreeSave"
      disagree-text="dialog.close"
      @agree="setProjectName"
      :is-input-dialog="true"
      header-shadow
    >
      <template #body>
        <v-row no-gutters align="baseline">
          <v-col cols="2"> {{ $i18n.t('dialog.name') }}: </v-col>
          <v-spacer></v-spacer>
          <v-col cols="5" class="mr-2">
            <data-input
              class="new__project__name"
              v-model="projectName"
              clearable
              :data-type="dataTypeText"
            ></data-input>
          </v-col>
        </v-row>
      </template>
    </main-large-dialog>
    <main-large-dialog
      v-if="isCreateProjectDialogLoaded"
      :is-visible.sync="isCreateProjectDialogVisible"
      :title="$i18n.t(`dialog.${isCreateProjectDialogCopy ? 'copyProject' : 'createProject'}`)"
      :agree-text="`dialog.${isCreateProjectDialogCopy ? 'agreeCopy' : 'create'}`"
      @agree="createProjectHandler"
      @modalClosed="isCreateProjectDialogCopy = false"
      :is-input-dialog="true"
      header-shadow
      :loading="isLoading"
    >
      <template #body>
        <v-row no-gutters align="baseline">
          <v-col cols="2"> {{ $i18n.t('dialog.name') }}: </v-col>
          <v-spacer></v-spacer>
          <v-col cols="5" class="mr-2">
            <data-input
              class="new__project__name"
              v-model="projectName"
              :hint="$i18n.t('dialog.newProjectFieldHint')"
              persistent-hint
              clearable
              :data-type="dataTypeText"
            ></data-input>
          </v-col>
        </v-row>
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isWarningDialogLoaded"
      :is-visible.sync="isWarningDialogVisible"
      :title="$i18n.t('dialog.unsavedChanges')"
      :body="$i18n.t('dialog.unsavedProjectChanges')"
    >
      <template #buttons>
        <v-spacer></v-spacer>
        <button-main-dialog cancel @click="isWarningDialogVisible = false" :text="$i18n.t('dialog.disagree')" />
        <button-main-dialog @click="onWarningDiscard" :text="$i18n.t('dialog.discard')" color="error" />
        <button-main-dialog @click="onWarningAgree" :text="$i18n.t('dialog.agreeSave')" />
      </template>
    </main-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.deleteProject')"
      :body="$i18n.t('dialog.confirmProjectDelete')"
      agree-text="dialog.agreeDelete"
      @agree="deleteProjectHandler"
      :loading="isLoading"
    ></main-dialog>
    <main-large-dialog
      v-if="isShareDialogLoaded"
      :is-visible.sync="isShareDialogVisible"
      :title="$i18n.t('dialog.shareProject')"
      agree-text="dialog.agreeShare"
      disagree-text="dialog.close"
      @agree="shareProjectHandler"
      :is-input-dialog="true"
      header-shadow
      :loading="isLoading"
    >
      <template #body>
        <v-row no-gutters align="start">
          <v-col cols="2" class="pt-3"> {{ $i18n.t('dialog.user') }}: </v-col>
          <v-spacer></v-spacer>
          <v-col cols="5" class="mr-2 mb-5">
            <data-input
              multiple
              v-model="emailsShare"
              :items="flteredUsers"
              item-text="name"
              item-value="id"
              :hint="$i18n.t('dialog.chooseUserToShareProject')"
              persistent-hint
              :data-type="dataTypeSelect"
              :autoConvertSelect="false"
            >
            </data-input>
          </v-col>
        </v-row>
      </template>
    </main-large-dialog>
    <layers-adding
      :is-layers-adding-visible.sync="isAddLayersDialogVisible"
      :layers="projectLayersList"
      :layersAll="layersAllArray"
      @addLayers="addLayersHandler"
      :loading="isAddingLayerLoading"
    />
    <main-input-dialog
      v-if="isAddGroupDialogLoaded"
      :is-visible.sync="isAddGroupDialogVisible"
      :title-translation="$i18n.t('dialog.addGroup')"
      agree-text="dialog.agreeAdd"
      @agree="addGroupHandler"
      :inputs="addGroupInputs"
    />
    <main-large-dialog
      v-if="isCreateProjectFromDefaultDialogLoaded"
      :is-visible.sync="isCreateProjectFromDefaultDialogVisible"
      :title="$i18n.t('dialog.createProject')"
      agree-text="dialog.create"
      @agree="createProjectFromDefaultHandler"
      :is-input-dialog="true"
      :loading="isLoading"
    >
      <template #body>
        <v-subheader class="text-left px-0">
          {{ createDialogBody }}
        </v-subheader>
        <data-input
          v-model="projectName"
          :label="$i18n.t('dialog.projectName')"
          clearable
          :data-type="dataTypeText"
        ></data-input>
      </template>
    </main-large-dialog>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DotsMenu from '@/components/DotsMenu';
import LegendLayersList from '@/components/LegendLayersList';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import ButtonMainDialog from './ButtonMainDialog';
import SidebarRow from '@/components/SidebarRow';
import LayersAdding from '@/components/LayersAdding';

export default {
  name: 'TheLegend',
  components: {
    SidebarRow,
    CardTitleRow,
    DataInput,
    DotsMenu,
    LegendLayersList,
    MainLoader,
    ButtonMainDialog,
    MainDialog: () => import('@/components/MainDialog'),
    LayersAdding,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    groupUniqueId: 0,
    dataTypeText: {
      name: 'text',
    },
    isAddingLayerLoading: false,
    projectEditingLoading: false,
    hasUnsavedChanges: false,
    projectName: '',
    projectProxy: undefined,
    wasWarningDisplayed: false,
    emailsShare: '',
    dataTypeSelect: {
      name: 'select',
    },
    dotsMenuItems: [
      {
        name: 'changeName',
      },
      {
        name: 'share',
      },
      {
        name: 'copy',
      },
      {
        name: 'delete',
      },
    ],
    isChangeNameDialogVisible: false,
    isChangeNameDialogLoaded: false,
    isWarningDialogVisible: false,
    isWarningDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isCreateProjectDialogCopy: false,
    isCreateProjectDialogVisible: false,
    isCreateProjectDialogLoaded: false,
    isShareDialogVisible: false,
    isShareDialogLoaded: false,
    isAddLayersDialogVisible: false,
    isAddGroupDialogVisible: false,
    isAddGroupDialogLoaded: false,
    isCreateProjectFromDefaultDialogVisible: false,
    isCreateProjectFromDefaultDialogLoaded: false,
    isLoading: false,
  }),
  watch: {
    zoom(nV) {
      if (nV !== this.currentProject.zoom) {
        this.isMapViewChanged = true;
      } else {
        this.hasUnsavedChanges = false;
      }
    },
    mapCenter(nV) {
      if (JSON.stringify(nV) !== JSON.stringify(this.currentProject.map_center.coordinates)) {
        this.isMapViewChanged = true;
      }
    },
    project: {
      immediate: true,
      handler(nV) {
        this.projectProxy = JSON.parse(JSON.stringify(nV));
      },
    },
  },
  computed: {
    zoom: get('map/zoom'),
    mapCenter: get('map/center'),
    project: sync('layers/project'),
    currentProject: get('layers/projects@:currentProjectId'),
    currentProjectId: get('layers/project.id'),
    isModifySnappingActive: get('edit/isModifySnappingActive'),
    unchangedProject: get('layers/unchangedProject'),
    projects: sync('layers/projects'),
    layers: sync('layers/project@layers'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    isMapViewChanged: sync('map/isMapViewChanged'),
    isUnsavedChangeTextVisible() {
      return (this.isMapViewChanged || this.hasUnsavedChanges) && (!this.isProjectDefault || !this.isSharedProject)
        ? this.$i18n.t('sidebar.unsavedChanges')
        : '';
    },
    isSaveButtonDisabled() {
      return (
        !this.isProjectLayersFetched ||
        (!this.isMapViewChanged && !this.hasUnsavedChanges && !this.isProjectDefault && !this.isSharedProject)
      );
    },
    projectLayersList() {
      return this.$_getFlatGroupsLayers(this.layers || []).layers;
    },
    groups() {
      return this.$_getFlatGroupsLayers(this.layers).groups;
    },
    groupsNames() {
      return this.groups.map(group => group.name);
    },
    addGroupInputs() {
      return [
        {
          name: 'name',
          value: '',
          translationPath: 'dialog.groupName',
          cols: '12',
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.groupsNames.includes(v) || 'rules.alreadyGroupWithName',
          ],
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    changeProjectTooltip() {
      return this.isModifySnappingActive
        ? this.$i18n.t('button.toChangeProject', { reason: this.$i18n.t('button.turnOffEditing') })
        : this.$i18n.t('button.changeProject');
    },
    createProjectTooltip() {
      return this.isModifySnappingActive
        ? this.$i18n.t('button.toCreateProject', { reason: this.$i18n.t('button.turnOffEditing') })
        : this.$i18n.t('button.createProject');
    },
    layersAllArray() {
      return this.$_objectToArray(this.$store.get('layers/layers'));
    },
    isProjectLayersFetched: sync('sidebar/isProjectLayersFetched'),
    users: get('users/users'),
    usersArray() {
      return this.$_objectToArray(this.users);
    },
    flteredUsers() {
      return this.usersArray.filter(user => user.id !== this.currentUserId);
    },
    currentUserId: get('users/currentUser@id'),
    projectsArray() {
      return this.$_sortObjectsByAttribute([...this.$_objectToArray(this.projects)], 'name', {
        name: 'role',
        value: 'default',
      });
    },
    isDisplayWarning() {
      return this.hasUnsavedChanges && !this.wasWarningDisplayed;
    },
    isProjectDefault() {
      return this.project && this.project.role === 'default';
    },
    isSharedProject() {
      return this.currentUserId !== this.project?.owner;
    },
    isAddLayerDisabled() {
      return this.isSharedProject || this.isProjectDefault || this.isModifySnappingActive;
    },
    addLayerTooltip() {
      if (this.isModifySnappingActive) {
        return this.$i18n.t('button.toAddLayer', { reason: this.$i18n.t('button.turnOffEditing') });
      }
      if (this.isSharedProject && !this.isProjectDefault) {
        return this.$i18n.t('sidebar.sharedProjectAddDisabled');
      } else if (this.isProjectDefault) {
        return this.$i18n.t('sidebar.defaultProjectAddDisabled');
      }
      return this.$i18n.t('button.addLayer');
    },
    addGroupTooltip() {
      if (this.isModifySnappingActive) {
        return this.$i18n.t('button.toAddGroup', { reason: this.$i18n.t('button.turnOffEditing') });
      }
      if (this.isSharedProject && !this.isProjectDefault) {
        return this.$i18n.t('sidebar.sharedProjectAddGroupDisabled');
      } else if (this.isProjectDefault) {
        return this.$i18n.t('sidebar.defaultProjectAddGroupDisabled');
      }
      return this.$i18n.t('button.addGroup');
    },
    saveProjectTooltip() {
      if (this.isSharedProject && !this.isProjectDefault) {
        return this.$i18n.t('sidebar.sharedProjectSave');
      } else if (this.isProjectDefault) {
        return this.$i18n.t('sidebar.defaultProjectSave');
      } else if (!this.hasUnsavedChanges) {
        return this.$i18n.t('sidebar.noChanges');
      }
      return this.$i18n.t('button.saveChanges');
    },
    createDialogBody() {
      return this.isProjectDefault
        ? this.$i18n.t('dialog.body.confirmProjectCreateFromDefault')
        : this.$i18n.t('dialog.body.confirmProjectCreateFromShared');
    },
  },
  methods: {
    createGtagEvent: call('gtag/createEvent'),
    editProject: call('layers/editProject'),
    shareProject: call('layers/shareProject'),
    createProject: call('layers/createProject'),
    deleteProject: call('layers/deleteProject'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    openCreateProjectDialog() {
      this.isCreateProjectFromDefaultDialogVisible = true;
      this.isCreateProjectFromDefaultDialogLoaded = true;

      this.createGtagEvent({
        action: 'open_project_create_dialog',
        category: 'map_legend',
      });
    },
    projectChanged(e) {
      if (e) {
        if (this.$route.query.project === e.id) return;
        this.project = e;
        this.isProjectLayersFetched = false;
        this.hasUnsavedChanges = false;
        this.$root.$emit('fetchProject');
      }
    },
    onWarningAgree() {
      this.isWarningDialogVisible = false;
      this.isWarningDialogLoaded = false;
      this.wasWarningDisplayed = true;
      this.saveProjectEditing();
    },
    onWarningDiscard() {
      this.isWarningDialogVisible = false;
      this.isWarningDialogLoaded = false;
      this.wasWarningDisplayed = true;
      this.discardProjectEditing();
    },
    setCurrentLayer(layer) {
      this.$router.push({
        name: 'layerTable',
        params: {
          lid: layer.id,
        },
      });
    },
    async saveProjectEditing() {
      this.projectCopy = JSON.parse(JSON.stringify(this.project));
      const payload = JSON.parse(JSON.stringify(this.project));
      payload.layers = payload.layers.map(layer => {
        if (typeof layer.id === 'string' && layer.id?.includes('ugi-')) {
          delete layer.id;
        }
        return { ...layer };
      });

      this.createGtagEvent({
        action: 'save_project_editing',
        category: 'map_legend',
        params: {
          name: this.project.name,
          id: this.project.id,
        },
      });

      try {
        this.projectEditingLoading = true;
        await this.editProject({ body: payload, id: this.project.id });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successProjectEdit'),
        });
        this.hasUnsavedChanges = false;
        this.isMapViewChanged = false;
        this.wasWarningDisplayed = false;
        this.projectEditingLoading = false;
      } catch (e) {
        console.log(e);
        this.projectEditingLoading = false;
      }
    },
    createProjectFromDefaultHandler() {
      this.createProjectHandler(true);
    },
    discardProjectEditing() {
      this.$store.set('layers/SET_PROJECT!', { ...this.unchangedProject });
      this.$store.set('layers/DISCARD_PROJECT!', { ...this.unchangedProject });
      this.$root.$emit('discardProjectLayers');
      this.hasUnsavedChanges = false;
      this.wasWarningDisplayed = false;
    },
    showShareDialog() {
      this.isShareDialogVisible = true;
      this.isShareDialogLoaded = true;

      this.createGtagEvent({
        action: 'open_project_share_dialog',
        category: 'map_legend',
      });
    },
    async shareProjectHandler() {
      if (this.emailsShare.length > 0) {
        try {
          this.isLoading = true;
          await this.shareProject({ id: this.project.id, userIds: this.emailsShare });
          this.isShareDialogVisible = false;
          this.isShareDialogLoaded = false;
          this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
            message: this.$i18n.t('success.successShareProject'),
          });
          this.emailsShare = [];
        } catch (error) {
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: this.$i18n.t(`error['${error.message}']`),
          });
        } finally {
          this.isLoading = false;
        }
      } else {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('dialog.userNotFound'),
        });
      }
    },
    async deleteProjectHandler() {
      this.createGtagEvent({
        action: 'delete_project',
        category: 'map_legend',
        params: {
          name: this.project.name,
          id: this.project.id,
        },
      });

      try {
        this.isLoading = true;
        await this.deleteProject(this.project.id);
        this.project = this.projectsArray[0];
        this.projectChanged();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successProjectDelete'),
        });
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoaded = false;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    showProjectDeleteDialog() {
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;

      this.createGtagEvent({
        action: 'open_project_delete_dialog',
        category: 'map_legend',
      });
    },
    showWarning() {
      this.$refs.select.isMenuActive = false;
      this.isWarningDialogVisible = true;
      this.isWarningDialogLoaded = true;
    },
    setProjectName() {
      if (!this.projectName) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.projects.nameRequired'),
        });
      } else {
        this.$store.set('layers/SET_PROJECT_NAME!', this.projectName);
        this.projectName = '';
        this.hasUnsavedChanges = true;
        this.isChangeNameDialogVisible = false;
        this.isChangeNameDialogLoaded = false;
      }
    },
    changeProjectName() {
      this.projectName = this.project.name;
      this.isChangeNameDialogVisible = true;
      this.isChangeNameDialogLoaded = true;
    },
    async createProjectHandler(asDefault = false) {
      if (!this.projectName) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.projects.nameRequired'),
        });
        return;
      }
      try {
        this.isLoading = true;
        const payload = {
          coordinateSystemEpsg: this.$_config.defaultEpsg,
          center: this.$store.get('map/center'),
          zoom: this.$store.get('map/zoom'),
          name: this.projectName,
        };
        if (asDefault || this.isCreateProjectDialogCopy) {
          payload.layers = this.project.layers;
        }
        await this.createProject(payload);
        this.projectChanged();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successCreateProject'),
        });
        this.projectName = '';
        this.isCreateProjectDialogVisible = false;
        this.isCreateProjectDialogLoaded = false;
        this.isCreateProjectDialogCopy = false;
        this.isCreateProjectFromDefaultDialogVisible = false;
        this.isCreateProjectFromDefaultDialogLoaded = false;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    showCreateProjectDialog(e, copy = false) {
      if (copy) {
        this.isCreateProjectDialogCopy = copy;
      }
      this.isCreateProjectDialogVisible = true;
      this.isCreateProjectDialogLoaded = true;
    },
    showAddLayersDialog() {
      this.isAddLayersDialogVisible = true;

      this.createGtagEvent({
        action: 'open_add_layer_dialog',
        category: 'map_legend',
      });
    },
    showAddGroupDialog() {
      this.isAddGroupDialogLoaded = true;
      this.isAddGroupDialogVisible = true;
    },
    addGroupHandler() {
      const value = JSON.parse(JSON.stringify(this.addGroupInputs.find(input => input.name === 'name').value));
      this.$store.set('layers/ADD_PROJECT_GROUP!', { value, id: `ugi-${this.groupUniqueId}` });
      this.groupUniqueId++;
      this.isAddGroupDialogVisible = false;
      this.addGroupInputs.find(input => input.name === 'name').value = '';
      this.$forceUpdate();
    },
    async addLayersHandler(layersChecked) {
      this.isAddingLayerLoading = true;
      const layersToAdd = layersChecked.map(layer => {
        const layerCopy = { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer };
        delete layerCopy.key;
        delete layerCopy.style_qgis;
        delete layerCopy.style_qgis_file_name;
        if (layer.type === 'features_layer') {
          layerCopy.labels_visible = layerCopy.labels_visible || false;
          layerCopy.style = layerCopy.style_web;
          layerCopy.cache = layerCopy.schema === 'cache' ? true : false;
          delete layerCopy.style_web;
          delete layerCopy.schema;
        }
        return layerCopy;
      });
      this.$store.set('layers/ADD_PROJECT_LAYERS!', layersToAdd);
      // const promises = layersToAdd
      //   .filter(layer => layer.type === 'features_layer' && !layer.supports_mvt)
      //   .map(layer => this.getLayerFeatures({ layer_id: layer.id }));
      // await Promise.all(promises);
      this.$root.$emit('pushProjectLayers', layersToAdd);
      this.isAddingLayerLoading = false;
      this.hasUnsavedChanges = true;
      this.layersChecked = [];
      this.isAddLayersDialogVisible = false;

      layersChecked.forEach(layer => {
        this.createGtagEvent({
          action: 'add_layer',
          category: 'map_legend',
          params: {
            type: layer.type,
            name: layer.name,
            id: layer.id,
          },
        });
      });
    },
  },
  mounted() {
    this.$root.$off('addLayersHandler');
    this.$root.$on('addLayersHandler', this.addLayersHandler);
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border(bottom);
}
.v-menu__content {
  max-width: 30% !important;
}
::v-deep .new__project__name .v-input__slot {
  margin-bottom: 0;
}
::v-deep .new__project__name .v-messages__message {
  color: rgba(0, 0, 0, 0.59);
  font-size: 10px;
  font-style: italic;
}
::v-deep .new__project__name .v-text-field__details {
  padding: 0;
}
::v-deep .v-messages__message {
  font-size: 10px;
  font-style: italic;
}
::v-deep .v-input__slot {
  margin-bottom: 3px;
}
::v-deep .main-dialog {
  .v-btn.down {
    margin-left: 14px !important;
    min-width: 90px;
    text-transform: initial;
    &.cancel {
      border-color: rgba(17, 10, 10, 0.2);
      &:hover {
        box-shadow: 0 3px 3px -2px rgba(0, 0, 0, 0.2), 0 3px 4px 0 rgba(0, 0, 0, 0.14), 0 1px 8px 0 rgba(0, 0, 0, 0.12) !important;
      }
    }
  }
}
</style>
